import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { RefreshCw, AlertTriangle, ChevronDown, ChevronRight, Users, ShoppingBag, Repeat, Clock, Download, Flag } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const HUE = '#4f46e5';        // tek seri / büyüklük rengi (indigo-600)
const HUE_SOFT = '#eef2ff';   // aynı hue'nun en açık basamağı — kohort tablosu tonlaması

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtTL = (n, digits = 0) =>
  n == null ? '—' : `₺${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: digits })}`;
const fmtTLShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `₺${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (Math.abs(v) >= 1e3) return `₺${Math.round(v / 1e3)}B`;
  return `₺${Math.round(v)}`;
};
const fmtPct = (n, d = 1) => (n == null ? '—' : `%${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: d })}`);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('tr-TR') : '—');

/* Sipariş sayısına göre müşteri dağılımının etiketi. Backend 5'te kesiyor. */
const bucketLabel = (b) => (b >= 5 ? '5+ sipariş' : `${b} sipariş`);

function Tile({ icon: Icon, label, value, sub, tone = 'default' }) {
  const toneCls = tone === 'accent' ? 'text-indigo-700' : 'text-gray-900';
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {Icon ? <Icon size={13} className="text-gray-400" /> : null}
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

function ChartTooltip({ active, payload, label, suffix }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-xs">
      <div className="text-gray-500">{label}{suffix || ''}</div>
      <div className="font-semibold text-gray-900 tabular-nums">
        {payload[0].name === 'customers' ? fmtInt(payload[0].value) : fmtTL(payload[0].value)}
      </div>
    </div>
  );
}

export default function High5Ltv() {
  const { selectedBrand } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [showCaveats, setShowCaveats] = useState(true);
  const [custSort, setCustSort] = useState('revenue');
  const [hideSuspect, setHideSuspect] = useState(false);
  const [custLimit, setCustLimit] = useState(50);

  const load = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/high5-ltv/overview`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || `HTTP ${res.status}`);
      setData(d);
    } catch (e) {
      setData(null); setError(e?.message || 'Yüklenemedi');
    } finally { setLoading(false); }
  }, [selectedBrand?.id]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    if (!selectedBrand?.id) return;
    setSyncing(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/high5-ltv/sync`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || `HTTP ${res.status}`);
      await load();
    } catch (e) { setError(e?.message || 'Senkron başarısız'); }
    finally { setSyncing(false); }
  };

  const s = data?.summary;
  const cohorts = useMemo(() => data?.cohorts || [], [data?.cohorts]);

  // Kohort tablosunda hücre tonlaması: aynı hue, açıktan koyuya (büyüklük).
  const ltvRange = useMemo(() => {
    const vals = cohorts.map((c) => c.today).filter((v) => v != null);
    return vals.length ? [Math.min(...vals), Math.max(...vals)] : [0, 1];
  }, [cohorts]);
  const tint = (v) => {
    if (v == null) return undefined;
    const [lo, hi] = ltvRange;
    const t = hi > lo ? (v - lo) / (hi - lo) : 0;
    return { backgroundColor: `rgba(79, 70, 229, ${(0.05 + t * 0.16).toFixed(3)})` };
  };

  const customerRows = useMemo(() => {
    const rows = (data?.customers || []).filter((c) => (hideSuspect ? !c.lowValue : true));
    const key = custSort === 'orders' ? 'orders' : custSort === 'avgOrder' ? 'avgOrder' : 'revenue';
    return [...rows].sort((a, b) => (b[key] || 0) - (a[key] || 0));
  }, [data?.customers, custSort, hideSuspect]);

  const downloadCsv = () => {
    const head = 'user_id,siparis,ciro,ortalama_sepet,ilk_siparis,son_siparis,farkli_gun,gun_araligi,kanal,supheli,tuhaf_id';
    const body = customerRows.map((c) => [
      c.userId, c.orders, Math.round(c.revenue), Math.round(c.avgOrder),
      String(c.firstOrder).slice(0, 10), String(c.lastOrder).slice(0, 10),
      c.distinctDays, c.spanDays, `"${c.channel || '(kanal yok)'}"`,
      c.lowValue ? 'evet' : '', c.oddId ? 'evet' : '',
    ].join(',')).join('\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF' + head + '\n' + body], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `high5-tekrar-eden-musteriler-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const freqData = useMemo(
    () => (data?.frequency || []).map((f) => ({ ...f, label: bucketLabel(f.bucket) })),
    [data?.frequency],
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* başlık */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Müşteri LTV &amp; Tekrar Satın Alma</h1>
          <p className="text-sm text-gray-500 mt-1">
            GA4 BigQuery export'u, <span className="font-medium">user_id</span> ile müşteri bazında tekilleştirilmiş.
            {data?.meta?.syncedAt && (
              <> Son senkron: <span className="font-medium">{new Date(data.meta.syncedAt).toLocaleString('tr-TR')}</span>
                {' · '}{fmtInt(data.meta.syncedOrders)} sipariş</>
            )}
          </p>
        </div>
        <button
          onClick={sync} disabled={syncing || loading}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Senkron sürüyor…' : 'BigQuery\'den tazele'}
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>
      )}

      {/* sınırlar — sayfanın okunma biçimini belirlediği için üstte */}
      <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50">
        <button
          onClick={() => setShowCaveats((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-amber-900"
        >
          {showCaveats ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <AlertTriangle size={15} />
          Bu sayfadaki rakamların sınırları
        </button>
        {showCaveats && (
          <ul className="px-4 pb-3 pl-11 text-[13px] leading-relaxed text-amber-900 list-disc space-y-1">
            <li>
              <b>Tutarlar brüt ve KDV dahil.</b> GA4'te tek bir iade olayı yok — iade düşülmemiş durumda.
              Net LTV için ERP'den iade oranı alınıp toplam seviyede kalibre edilmesi gerekir.
            </li>
            <li>
              <b>Kanal kırılımı müşterilerin bir kısmında yok.</b> Çerez onayı reddedildiğinde GA4 kaynak
              bilgisi taşımıyor
              {data?.meta && (
                <> — {fmtInt(data.meta.unconsentedOrders)} sipariş ({fmtPct(
                  100 * data.meta.unconsentedOrders / Math.max(1, data.meta.consentedOrders + data.meta.unconsentedOrders), 0)})
                  onaysız</>
              )}. Ayrıca <code>collected_traffic_source</code> tamamen boş (gclid/kampanya yok),
              bu yüzden <b>kampanya seviyesinde LTV çıkarılamıyor</b>, sadece kaynak/ortam.
            </li>
            <li>
              <b>Yalnızca web.</b> Sipariş arkası sistem (Shopify/ikas/Woo) bağlı değil; mağaza ve
              çevrimdışı satış bu analizin dışında.
            </li>
            <li>
              <b>Olgunlaşmamış kohortlar maskeli.</b> Bir kohortun 30/60/90/180 günlük sütunu, o kohorttaki
              <i> her</i> müşteri o kadar gün gözlenmediyse boş bırakılır — yarım veriyle olgun kohortu
              kıyaslamak en sık yapılan hata.
              {data?.meta?.syntheticOrders > 0 && (
                <> Ayrıca {fmtInt(data.meta.syntheticOrders)} siparişte site sipariş numarası göndermemiş
                  (<code>(not set)</code>); bunlar müşteri+gün bazında ayrı sipariş sayıldı.</>
              )}
            </li>
          </ul>
        )}
      </div>

      {loading && !data && <div className="text-sm text-gray-500">Yükleniyor…</div>}
      {data?.empty && (
        <div className="px-4 py-8 text-center text-sm text-gray-600 bg-white border border-gray-200 rounded-lg">
          Henüz senkron çalışmadı. Yukarıdaki <b>BigQuery'den tazele</b> düğmesine bas.
        </div>
      )}

      {s && (
        <>
          {/* özet */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3">
            <Tile icon={Users} label="Müşteri" value={fmtInt(s.customers)}
                  sub={`${fmtDate(data.meta.firstOrder)} → ${fmtDate(data.meta.lastOrder)}`} />
            <Tile icon={ShoppingBag} label="Sipariş" value={fmtInt(s.orders)}
                  sub={`müşteri başına ${(s.orders / Math.max(1, s.customers)).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`} />
            <Tile label="Ciro" value={fmtTLShort(s.revenue)} sub="brüt, KDV dahil" />
            <Tile label="Sepet ortalaması" value={fmtTL(s.aov)} sub="sipariş başına" />
            <Tile label="Ortalama LTV" value={fmtTL(s.ltv)} tone="accent" sub="bugüne kadar gerçekleşen" />
            <Tile icon={Repeat} label="Tekrar oranı" value={fmtPct(s.repeatRate)} tone="accent"
                  sub={`${fmtInt(s.repeatCustomers)} müşteri 2+ sipariş`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Tile label="Tekrar edenlerin ciro payı" value={fmtPct(s.repeatRevenueShare)}
                  sub={`müşterilerin ${fmtPct(s.repeatRate)}'i`} />
            <Tile icon={Clock} label="2. siparişe medyan süre" value={`${fmtInt(s.medianDaysToSecond)} gün`}
                  sub={`ortalama ${Math.round(s.avgDaysToSecond)} gün`} />
            <Tile label="30 günde 2. sipariş" value={fmtPct(s.secondWithin30, 0)}
                  sub="ikinci siparişi verenlerin payı" />
            <Tile label="90 günde 2. sipariş" value={fmtPct(s.secondWithin90, 0)}
                  sub="LTV bu süre içinde olgunlaşıyor" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
            {/* kohort tablosu */}
            <div className="xl:col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">Kohort tablosu</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  İlk siparişin verildiği aya göre, müşteri başına gerçekleşen kümülatif ciro.
                  Boş hücre = kohort o kadar gün olgunlaşmadı.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                      <th className="text-left font-medium px-4 py-2">Kohort</th>
                      <th className="text-right font-medium px-3 py-2">Müşteri</th>
                      <th className="text-right font-medium px-3 py-2">İlk sipariş</th>
                      {[30, 60, 90, 180].map((d) => (
                        <th key={d} className="text-right font-medium px-3 py-2">{d} gün</th>
                      ))}
                      <th className="text-right font-medium px-3 py-2">Bugüne kadar</th>
                      <th className="text-right font-medium px-4 py-2">Tekrar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {cohorts.map((c) => {
                      const open = c.maturityDays < 0;
                      return (
                        <tr key={c.month} className="hover:bg-gray-50/60">
                          <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">
                            {c.month}
                            {open && <span className="ml-2 text-[10px] font-normal text-gray-400">ay sürüyor</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtInt(c.customers)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtTL(c.firstOrder)}</td>
                          {[['d30', 30], ['d60', 60], ['d90', 90], ['d180', 180]].map(([k, need]) => (
                            <td key={k}
                                className={`px-3 py-2 text-right tabular-nums ${c.maturityDays >= need ? 'text-gray-900' : 'text-gray-300'}`}
                                title={c.maturityDays >= need ? undefined : `Kohort ${need} günü doldurmadı (${c.maturityDays} gün)`}>
                              {c.maturityDays >= need ? fmtTL(c[k]) : '—'}
                            </td>
                          ))}
                          <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900" style={tint(c.today)}>
                            {fmtTL(c.today)}
                          </td>
                          <td className={`px-4 py-2 text-right tabular-nums ${c.maturityDays >= 30 ? 'text-gray-900' : 'text-gray-300'}`}
                              title={c.maturityDays >= 30 ? undefined : 'Kohort 30 günü doldurmadı — oran düşük görünür'}>
                            {fmtPct(c.repeatPct, 1)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* LTV eğrisi */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">LTV eğrisi</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Müşteri başına kümülatif ciro, ilk siparişten itibaren.
                  Yalnızca 180 günü dolmuş kohortlar ({fmtInt(data.meta.matureCustomers)} müşteri).
                </p>
              </div>
              <div className="p-3">
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={data.curve} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                    <defs>
                      <linearGradient id="ltvFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={HUE} stopOpacity={0.18} />
                        <stop offset="100%" stopColor={HUE} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="day" tickLine={false} axisLine={false}
                           tick={{ fontSize: 11, fill: '#9ca3af' }}
                           tickFormatter={(d) => `${d}g`} />
                    <YAxis tickLine={false} axisLine={false} width={64}
                           tick={{ fontSize: 11, fill: '#9ca3af' }}
                           tickFormatter={(v) => fmtTL(v)} />
                    <Tooltip content={<ChartTooltip suffix=". gün" />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }} />
                    <Area type="monotone" dataKey="value" stroke={HUE} strokeWidth={2}
                          fill="url(#ltvFill)" dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }} />
                  </AreaChart>
                </ResponsiveContainer>
                {data.curve?.length > 1 && (
                  <p className="px-1 pt-1 text-xs text-gray-500">
                    İlk sipariş {fmtTL(data.curve[0].value)} → 180. gün{' '}
                    <span className="font-medium text-gray-900">{fmtTL(data.curve[data.curve.length - 1].value)}</span>
                    {' '}(+{fmtPct(100 * (data.curve[data.curve.length - 1].value / Math.max(1, data.curve[0].value) - 1), 0)})
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {/* kanal */}
            <div className="xl:col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">İlk temas kanalına göre LTV</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Müşterinin siteyle ilk temas ettiği kaynak/ortam. En az 15 müşterisi olan kanallar.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                      <th className="text-left font-medium px-4 py-2">Kanal</th>
                      <th className="text-right font-medium px-3 py-2">Müşteri</th>
                      <th className="text-right font-medium px-3 py-2">Sepet ort.</th>
                      <th className="text-right font-medium px-3 py-2">Tekrar</th>
                      <th className="text-left font-medium px-4 py-2 w-[38%]">Ortalama LTV</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(data.channels || []).map((ch) => {
                      const max = Math.max(...(data.channels || []).map((x) => x.ltv || 0), 1);
                      return (
                        <tr key={ch.channel} className={`hover:bg-gray-50/60 ${ch.unknown ? 'bg-gray-50/50' : ''}`}>
                          <td className={`px-4 py-2 whitespace-nowrap ${ch.unknown ? 'text-gray-500 italic' : 'text-gray-900 font-medium'}`}>
                            {ch.channel}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtInt(ch.customers)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtTL(ch.aov)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtPct(ch.repeatPct)}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2.5 rounded-sm" style={{ backgroundColor: HUE_SOFT }}>
                                <div className="h-2.5 rounded-sm"
                                     style={{ width: `${Math.max(2, (ch.ltv / max) * 100)}%`, backgroundColor: ch.unknown ? '#c7d2fe' : HUE }} />
                              </div>
                              <span className="tabular-nums text-gray-900 font-medium w-20 text-right">{fmtTL(ch.ltv)}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* sipariş sıklığı */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">Sipariş sıklığı</h2>
                <p className="text-xs text-gray-500 mt-0.5">Kaç sipariş veren kaç müşteri var.</p>
              </div>
              <div className="p-3">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={freqData} margin={{ top: 16, right: 8, bottom: 4, left: 4 }}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                    <YAxis tickLine={false} axisLine={false} width={42}
                           tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={fmtInt} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Bar dataKey="customers" name="customers" fill={HUE} radius={[4, 4, 0, 0]} maxBarSize={44}>
                      <LabelList dataKey="customers" position="top"
                                 style={{ fontSize: 11, fill: '#6b7280' }} formatter={fmtInt} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="px-1 pt-1 text-xs text-gray-500">
                  Cironun <span className="font-medium text-gray-900">{fmtPct(s.repeatRevenueShare)}</span>'i
                  2+ sipariş veren <span className="font-medium text-gray-900">{fmtPct(s.repeatRate)}</span>'lik
                  kesimden geliyor.
                </div>
              </div>
            </div>
          </div>

          {/* tekrar eden müşteri listesi */}
          <div className="mt-4 bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  Tekrar eden müşteriler
                  <span className="ml-2 font-normal text-gray-500">{fmtInt(customerRows.length)} kişi</span>
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Pencerede 2 veya daha fazla sipariş veren her müşteri. user_id, sitenin kendi müşteri kaydı.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                  <input type="checkbox" checked={hideSuspect} onChange={(e) => setHideSuspect(e.target.checked)}
                         className="rounded border-gray-300" />
                  Şüpheli hesapları gizle
                </label>
                <select value={custSort} onChange={(e) => setCustSort(e.target.value)}
                        className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white">
                  <option value="revenue">Ciroya göre</option>
                  <option value="orders">Sipariş sayısına göre</option>
                  <option value="avgOrder">Sepet ortalamasına göre</option>
                </select>
                <button onClick={downloadCsv}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50">
                  <Download size={13} /> CSV
                </button>
              </div>
            </div>

            {data.meta?.suspects?.count > 0 && (
              <div className="px-4 py-2.5 bg-amber-50/70 border-b border-amber-100 text-[13px] text-amber-900 flex items-start gap-2">
                <Flag size={14} className="mt-0.5 shrink-0" />
                <span>
                  <b>{data.meta.suspects.count} hesap işaretli.</b> 3+ siparişi olup sepet ortalaması ₺1.500'ün
                  altında kalanlar — sitenin geneli {fmtTL(s.aov)}, bu profil test/personel hesabına benziyor.
                  Toplam {fmtInt(data.meta.suspects.orders)} sipariş / {fmtTL(data.meta.suspects.revenue)}.
                  Çıkarılırsa ortalama LTV {fmtTL(data.meta.suspects.cleanLtv)} (şu an {fmtTL(s.ltv)}),
                  tekrar oranı {fmtPct(data.meta.suspects.cleanRepeatRate)} (şu an {fmtPct(s.repeatRate)}) olur —
                  yani <b>başlık rakamlarını bozmuyorlar</b>, sadece bu listenin sıralamasını.
                </span>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                    <th className="text-left font-medium px-4 py-2">user_id</th>
                    <th className="text-right font-medium px-3 py-2">Sipariş</th>
                    <th className="text-right font-medium px-3 py-2">Ciro</th>
                    <th className="text-right font-medium px-3 py-2">Sepet ort.</th>
                    <th className="text-left font-medium px-3 py-2">İlk → Son</th>
                    <th className="text-right font-medium px-3 py-2">Aralık</th>
                    <th className="text-left font-medium px-4 py-2">İlk temas kanalı</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {customerRows.slice(0, custLimit).map((c) => (
                    <tr key={c.userId} className={`hover:bg-gray-50/60 ${c.lowValue ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-4 py-2 font-mono text-[12px] text-gray-700 whitespace-nowrap">
                        {c.userId}
                        {c.lowValue && (
                          <span className="ml-2 align-middle inline-flex items-center gap-1 text-[10px] font-sans font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                                title="3+ sipariş, sepet ortalaması ₺1.500 altı">şüpheli</span>
                        )}
                        {c.oddId && (
                          <span className="ml-1.5 align-middle inline-flex items-center text-[10px] font-sans font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
                                title="user_id, site müşteri kaydı biçiminde değil (24 hane hex bekleniyor)">tuhaf id</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">{fmtInt(c.orders)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">{fmtTL(c.revenue)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${c.lowValue ? 'text-amber-800 font-medium' : 'text-gray-600'}`}>
                        {fmtTL(c.avgOrder)}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap text-[13px]">
                        {fmtDate(c.firstOrder)} → {fmtDate(c.lastOrder)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600"
                          title={`${c.distinctDays} farklı günde sipariş`}>
                        {fmtInt(c.spanDays)} gün
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap ${c.channel ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                        {c.channel || 'kanal yok — çerez onayı reddedildi'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {customerRows.length > custLimit && (
              <button onClick={() => setCustLimit((n) => n + 100)}
                      className="w-full px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 border-t border-gray-100">
                {fmtInt(customerRows.length - custLimit)} müşteri daha göster
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
